import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from 'react'
import {
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import SwipeIcon from '@mui/icons-material/Swipe'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import { EffectCoverflow, Pagination } from 'swiper/modules'

import ApiService    from '../../common/ApiCalls'
import NoImagePlaceholder from '../../v2/components/NoImage'
import { carouselImageSize, useQuickView } from '../../v2/hooks/use-quick-view.hook'

import './styles.scss';
import { useSettingsSelector } from '../../v2/store/settings'

export default function AssetImageCarousel ({
  assetId,
  addressCoords,
  editable = true,
  variant  = 'full',
  onImageChange = null,
  pendingImagesToAdd = null,
  pendingImagesToDelete = null,
  onRefreshComplete = null
}) {
  const theme      = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const fileInput  = useRef(null)
  const swiperRef  = useRef(null)

  const [allImages, setAllImages] = useState([])
  const [loading,   setLoading]   = useState(true)

  const [pendingAdd, setPendingAdd] = useState([]);
  const [pendingDelete, setPendingDelete] = useState([]);

  const actualPendingAdd = pendingImagesToAdd || pendingAdd;
  const actualPendingDelete = pendingImagesToDelete || pendingDelete;

  const MAX_WINDOW = 7
  const [windowStart, setWindowStart] = useState(0)

  const [newUrl,      setNewUrl]      = useState('')
  const [selectedImg, setSelectedImg] = useState(null)
  const [toast,       setToast]       = useState({ open: false, text: '', severity: 'info' })
  const [confirm,     setConfirm]     = useState({ open: false, imgId: null })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [showSwipeHint, setShowSwipeHint] = useState(false)
  const swipeHintShown = useRef(false)

  const { getGoogleStreetViewImage } = useQuickView();
  const { isGoogleImagePull } = useSettingsSelector();
  
  const displayImages = useMemo(() => {
    const images = [...allImages]
      .filter(img => !actualPendingDelete.includes(img.id))
      .concat(actualPendingAdd.map(item => ({
        id: item.tempId,
        url: item.url,
        isPending: true
      })));

    return images.length ? images : [];
  }, [allImages, actualPendingAdd, actualPendingDelete]);
  const hasMultipleImages = displayImages.length > 1

  useEffect(() => {
    if (!hasMultipleImages) {
      setShowSwipeHint(false)
      return undefined
    }

    if (swipeHintShown.current) return undefined

    swipeHintShown.current = true
    setShowSwipeHint(true)

    const timeout = window.setTimeout(() => setShowSwipeHint(false), 3200)
    return () => window.clearTimeout(timeout)
  }, [hasMultipleImages])

  const loadImages = useCallback(async () => {
    const res = await ApiService().getImageByAssetId(assetId)
    const imgs = res?.data?.data?.images ?? []

    if (!imgs?.length && addressCoords && isGoogleImagePull) {
      const url = getGoogleStreetViewImage(addressCoords, carouselImageSize.full);

      imgs.push({
        id: 'street-view',
        url: url
      });
    }

    setAllImages(imgs)

    if (variant === 'full') {
      const mid = Math.round((imgs.length - 1) / 2)
      setWindowStart(Math.max(0, mid - Math.floor(MAX_WINDOW / 2)))
    } else {
      setWindowStart(0)
    }

    return imgs;
  }, [assetId, isGoogleImagePull, variant])

  useEffect(() => {
    let mounted = true
    ;(async () => { try { await loadImages() } finally { mounted && setLoading(false) } })()
    return () => { mounted = false }
  }, [loadImages])

  const windowImages = useMemo(() =>
      variant === 'full' && displayImages.length > MAX_WINDOW
        ? displayImages.slice(windowStart, windowStart + MAX_WINDOW)
        : displayImages
    , [displayImages, windowStart, variant])

  const slidesPerView =
    variant === 'compact'
      ? 1
      : windowImages.length > 3
        ? Math.min(windowImages.length, 7)
        : windowImages.length || 1

  const initialSlide =
    variant === 'compact'
      ? 0
      : Math.round((windowImages.length - 1) / 2)

  const pushToast = (text, severity = 'info') =>
    setToast({ open: true, text, severity })

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await loadImages();
      pendingAdd.forEach((img) => { if (img.type === 'file') URL.revokeObjectURL(img.url); });
      setPendingAdd([]);
      setPendingDelete([]);
      return true;
    } catch (err) {
      pushToast(err.message || 'Unexpected error', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadImages, pendingAdd]);

  useEffect(() => {
    if (typeof onRefreshComplete === 'function') {
      onRefreshComplete(reload);
    }
  }, [reload, onRefreshComplete]);

  const handleAddUrl = async () => {
    const trimmed = newUrl.trim()
    if (!trimmed) return

    const newImage = {
      tempId: `pending-url-${Date.now()}`,
      url: trimmed,
      type: 'url'
    }

    if (onImageChange) {
      onImageChange({ type: 'add', image: newImage })
    } else {
      setPendingAdd(prev => [...prev, newImage])
    }

    setNewUrl('')
  }

  const handleFileChange = async e => {
    const file = e.target.files?.[0]
    if (!file || !/\.(jpe?g|png|gif|bmp|webp)$/i.test(file.name)) {
      pushToast('Please choose a valid image file', 'warning')
      return
    }

    const newImage = {
      tempId: `pending-file-${Date.now()}`,
      file: file,
      url: URL.createObjectURL(file),
      type: 'file'
    }

    if (onImageChange) {
      onImageChange({ type: 'add', image: newImage })
    } else {
      setPendingAdd(prev => [...prev, newImage])
    }

    e.target.value = ''
  }

  const requestDelete = imgId => {
    if (typeof imgId === 'string' && imgId.startsWith('pending')) {
      const fileUrl = actualPendingAdd.find(img => (img.tempId === imgId && img.type === 'file'))?.url;
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }

      if (onImageChange) {
        onImageChange({ type: 'remove-pending', tempId: imgId })
      } else {
        setPendingAdd(prev => prev.filter(img => img.tempId !== imgId))
      }
      return
    }

    setConfirm({ open: true, imgId })
  }

  const confirmDelete = () => {
    setConfirm({ open: false, imgId: null })

    if (onImageChange) {
      onImageChange({ type: 'delete', imageId: confirm.imgId })
    } else {
      setPendingDelete(prev => [...prev, confirm.imgId])
    }
  }

  const handleSlideChange = (s) => {
    setShowSwipeHint(false)

    if (variant !== 'full' || allImages.length <= MAX_WINDOW) return

    const idx = s.activeIndex
    const buffer = 2

    if (isTransitioning) return;

    if (idx <= buffer && windowStart > 0) {
      setIsTransitioning(true);
      setWindowStart(p => Math.max(0, p - 1))
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.slideTo(idx + 1, 0, false)
        }
        setIsTransitioning(false)
      }, 10)
      return
    }

    if (
      idx >= windowImages.length - (buffer + 1) &&
      windowStart + MAX_WINDOW < allImages.length
    ) {
      setIsTransitioning(true)
      setWindowStart(p => Math.min(allImages.length - MAX_WINDOW, p + 1))
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.slideTo(idx - 1, 0, false)
        }
        setIsTransitioning(false)
      }, 10)
    }
  }

  const handleTransitionEnd = (s) => {
    if (isTransitioning) return
    handleSlideChange(s)
  }

  if (loading) return <CircularProgress />

  return (
    <>
      <div
        className={`asset-carousel ${ variant === 'compact' ? 'asset-carousel--compact' : 'asset-carousel--full' }`}
      >
        {displayImages.length ? <Swiper
          className="asset-carousel__swiper"
          modules={[EffectCoverflow, Pagination]}
          effect="coverflow"
          grabCursor
          centeredSlides
          slidesPerView={slidesPerView}
          initialSlide={initialSlide}
          onSwiper={s => (swiperRef.current = s)}
          onSliderMove={() => setShowSwipeHint(false)}
          onSlideChange={handleSlideChange}
          onSlideChangeTransitionEnd={handleTransitionEnd}
          coverflowEffect={{ rotate: 30, depth: 100, slideShadows: true }}
          pagination={{ clickable: true }}
        >
          {displayImages.map(img => (
            <SwiperSlide key={img.id}>
              <img
                src={img.url}
                alt=""
                onClick={() => setSelectedImg(img.url)}
                className={img.isPending ? 'asset-carousel__pending-image' : ''}
              />
              {editable && img.id !== 'placeholder' && (
                <IconButton
                  aria-label="Delete image"
                  size="small"
                  className="asset-carousel__delete-btn"
                  onClick={e => { e.stopPropagation(); requestDelete(img.id) }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {img.isPending && (
                <div className="asset-carousel__pending-indicator">Pending</div>
              )}
            </SwiperSlide>
          ))}
        </Swiper> : <div className='no-image-wrapper'><NoImagePlaceholder /></div>}

        {showSwipeHint && (
          <div
            className="asset-carousel__swipe-hint"
            role="status"
            aria-label="Swipe left or right to view more images"
          >
            <SwipeIcon className="asset-carousel__swipe-icon" aria-hidden="true" />
            <span>Swipe</span>
          </div>
        )}

        {editable && (
          <div className="asset-carousel__controls">
            <TextField
              className="asset-carousel__url-input"
              label="Add image URL"
              size="small"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
            />
            <Button variant="contained" onClick={handleAddUrl}>
              Upload URL
            </Button>
            <input
              type="file"
              accept="image/*"
              ref={fileInput}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button variant="outlined" onClick={() => fileInput.current.click()}>
              Browse…
            </Button>
          </div>
        )}

        <Dialog
          open={Boolean(selectedImg)}
          onClose={() => setSelectedImg(null)}
          fullScreen={fullScreen}
          PaperProps={{ style: { background: '#000' } }}
        >
          <IconButton
            aria-label="Close image preview"
            className="asset-carousel__close-btn"
            onClick={() => setSelectedImg(null)}
          >
            <CloseIcon />
          </IconButton>
          {selectedImg && (
            <img
              src={selectedImg}
              alt="preview"
              className="asset-carousel__dialog-img"
            />
          )}
        </Dialog>
      </div>

      <Dialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, imgId: null })}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, imgId: null })}>
            Cancel
          </Button>
          <Button color="secondary" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast({ ...toast, open: false })}
          variant="filled"
        >
          {toast.text}
        </Alert>
      </Snackbar>
    </>
  )
}
